import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Box,
  Heading,
  HStack,
  FormControl,
  FormLabel,
  Select,
  Text,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartBar } from "@fortawesome/free-solid-svg-icons";

const getStatusText = (status) => {
  switch (status) {
    case "New":
      return "ใบจองใหม่";
    case "Cancelled":
      return "ยกเลิกการจอง";
    case "Processing":
      return "ระหว่างดำเนินการ";
    case "Approved":
      return "อนุมัติการจอง";
    case "ReadyForDelivery":
      return "พร้อมจัดส่ง";
    case "Received":
      return "รับวัคซีนแล้ว";
    default:
      return status || "";
  }
};

const VaccineReservationChart = ({ data, pickupPointOptions, viewMode }) => {
  const [selectedPickupPoint, setSelectedPickupPoint] = useState("");

  // จัดการการเปลี่ยนแปลงใน dropdown
  const handlePickupPointChange = (e) => {
    setSelectedPickupPoint(e.target.value);
  };

  // กรองคลังที่มีข้อมูลใน data
  const availablePickupPoints = [
    ...new Set(data.map((item) => item.PickupPointName)),
  ].filter(Boolean);
  const filteredPickupPointOptions = pickupPointOptions.filter(
    (point) =>
      point.WarehouseName &&
      availablePickupPoints.includes(point.WarehouseName)
  );

  // เตรียมข้อมูลสำหรับกราฟ
  const chartData = () => {
    if (viewMode === "byVaccine") {
      const groupedData = data.reduce((acc, item) => {
        const key = item.VaccineNameTH;
        if (!acc[key]) {
          acc[key] = {
            name: item.VaccineNameTH,
            requestQuantity: 0,
            approvedQuantity: 0,
            vaccineEN: item.VaccineNameEN,
            pickupPoint: selectedPickupPoint || "ทุกคลัง",
          };
        }
        if (!selectedPickupPoint || item.PickupPointName === selectedPickupPoint) {
          acc[key].requestQuantity += item.RequestQuantity;
          acc[key].approvedQuantity += item.ApprovedQuantity;
        }
        return acc;
      }, {});
      return Object.values(groupedData);
    } else {
      const groupedData = data.reduce((acc, item) => {
        const key = item.ReservationID;
        if (!acc[key]) {
          acc[key] = {
            name: item.ReservationID,
            requestQuantity: 0,
            approvedQuantity: 0,
            pickupPoint: item.PickupPointName,
            farmerName: item.FarmerName,
            status: getStatusText(item.ReservationStatus),
          };
        }
        if (!selectedPickupPoint || item.PickupPointName === selectedPickupPoint) {
          acc[key].requestQuantity += item.RequestQuantity;
          acc[key].approvedQuantity += item.ApprovedQuantity;
        }
        return acc;
      }, {});
      return Object.values(groupedData);
    }
  };

  // กำหนดสีสำหรับกราฟ
  const requestBarColor = "#8884d8"; // Purple
  const approvedBarColor = "#82ca9d"; // Green

  // Tooltip ที่กำหนดเอง
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          bg="white"
          p={3}
          border="1px solid #ccc"
          borderRadius="md"
          boxShadow="md"
        >
          <p>
            <strong>{viewMode === "byVaccine" ? "ชื่อวัคซีน (TH)" : "เลขที่ใบจอง"}:</strong> {label}
          </p>
          {viewMode === "byVaccine" && (
            <p>
              <strong>ชื่อวัคซีน (EN):</strong> {data.vaccineEN}
            </p>
          )}
          <p>
            <strong>คลังวัคซีน:</strong> {data.pickupPoint}
          </p>
          {viewMode === "byReservation" && (
            <>
              <p>
                <strong>ชื่อเกษตรกร:</strong> {data.farmerName}
              </p>
              <p>
                <strong>สถานะ:</strong> {data.status}
              </p>
            </>
          )}
          <p>
            <strong>จำนวนสั่งจอง:</strong> {data.requestQuantity}
          </p>
          <p>
            <strong>จำนวนอนุมัติ:</strong> {data.approvedQuantity}
          </p>
        </Box>
      );
    }
    return null;
  };

  return (
    <Box
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
      p={5}
      mt={6}
      bg="white"
    >
      <HStack>
        <FontAwesomeIcon icon={faChartBar} />
        <Heading size="md">กราฟยอดการสั่งจองวัคซีน</Heading>
      </HStack>
      <FormControl m={5} maxW="300px">
        <FormLabel fontSize="xl" color="gray">
          เลือกคลังวัคซีน
        </FormLabel>
        {filteredPickupPointOptions.length > 0 ? (
          <Select
            value={selectedPickupPoint}
            onChange={handlePickupPointChange}
            placeholder="ทุกคลัง"
            colorScheme="iBlue"
          >
            {filteredPickupPointOptions.map((point) => (
              <option key={point.WarehouseID} value={point.WarehouseName}>
                {point.WarehouseName}
              </option>
            ))}
          </Select>
        ) : (
          <Text color="gray.500">ไม่มีคลังที่มีข้อมูล</Text>
        )}
      </FormControl>
      {chartData().length > 0 ? (
        <ResponsiveContainer width="100%" height={500}>
          <BarChart
            data={chartData()}
            margin={{ top: 40, right: 30, left: 20, bottom: 60 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="name"
              angle={-45}
              textAnchor="end"
              interval={0}
              height={150}
              tick={{ fontSize: 12 }}
            />
            <YAxis
              label={{
                value: "จำนวน (หน่วย)",
                angle: -90,
                position: "insideLeft",
                offset: -5,
              }}
              tick={{ fontSize: 12 }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              layout="horizontal"
              verticalAlign="top"
              align="right"
              wrapperStyle={{ paddingBottom: 10 }}
            />
            <Bar
              dataKey="requestQuantity"
              name="ยอดสั่งจอง"
              fill={requestBarColor}
              stackId="a"
              barSize={25}
            />
            <Bar
              dataKey="approvedQuantity"
              name="ยอดอนุมัติ"
              fill={approvedBarColor}
              stackId="a"
              barSize={25}
            />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <Text textAlign="center" color="gray.500" p={5}>
          ไม่มีข้อมูลสำหรับแสดงกราฟ
        </Text>
      )}
    </Box>
  );
};

export default VaccineReservationChart;